import { API_BASE_URL } from '@/shared/config';
export class ApiError extends Error {
    status;
    path;
    method;
    constructor(message, options) {
        super(message);
        this.name = 'ApiError';
        this.status = options.status;
        this.path = options.path;
        this.method = options.method;
    }
}
const CSRF_HEADER = 'x-csrf-token';
const CSRF_MISMATCH_MESSAGE = 'Invalid CSRF token';
let csrfToken = null;
let onUnauthorized = null;
let csrfReload = null;
export function setCsrfToken(token) {
    csrfToken = token;
}
export function getCsrfToken() {
    return csrfToken;
}
export function setOnUnauthorized(handler) {
    onUnauthorized = handler;
}
function isStateChangingRequest(method) {
    return !['GET', 'HEAD', 'OPTIONS'].includes(method);
}
function rememberCsrfHeader(response) {
    const refreshedCsrf = response.headers.get(CSRF_HEADER);
    if (refreshedCsrf) {
        csrfToken = refreshedCsrf;
    }
}
async function reloadCsrfFromSession() {
    if (!csrfReload) {
        csrfReload = (async () => {
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
                credentials: 'include',
            });
            rememberCsrfHeader(response);
            if (!response.ok) {
                return;
            }
            if (response.headers.get(CSRF_HEADER)) {
                return;
            }
            const rawBody = await response.text();
            try {
                const body = JSON.parse(rawBody);
                if (typeof body.csrfToken === 'string' && body.csrfToken) {
                    csrfToken = body.csrfToken;
                }
            }
            catch {
                // Тело /me без токена не затирает значение в памяти.
            }
        })().finally(() => {
            csrfReload = null;
        });
    }
    await csrfReload;
}
function looksLikeHtml(body, contentType) {
    if (contentType.includes('text/html')) {
        return true;
    }
    const start = body.trimStart().slice(0, 15).toLowerCase();
    return start.startsWith('<!doctype html') || start.startsWith('<html');
}
function parseJsonBody(rawBody, contentType, path, method) {
    if (looksLikeHtml(rawBody, contentType)) {
        throw new ApiError('API кабинета не отвечает. Обновите страницу чуть позже.', { status: 502, path, method });
    }
    try {
        return JSON.parse(rawBody);
    }
    catch {
        throw new ApiError('API кабинета вернул неожиданный ответ. Обновите страницу.', { status: 502, path, method });
    }
}
export async function request(path, options = {}) {
    return requestOnce(path, options, false);
}
async function requestOnce(path, options, csrfRetried) {
    const method = (options.method ?? 'GET').toUpperCase();
    const headers = new Headers(options.headers);
    if (options.body !== undefined &&
        !headers.has('Content-Type') &&
        !(options.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    if (isStateChangingRequest(method) && !csrfToken) {
        await reloadCsrfFromSession();
    }
    if (csrfToken && isStateChangingRequest(method)) {
        headers.set(CSRF_HEADER, csrfToken);
    }
    const response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        credentials: 'include',
        headers,
    });
    const contentType = response.headers.get('content-type') ?? '';
    const rawBody = await response.text();
    if (!response.ok) {
        let message = response.statusText || 'Request failed';
        try {
            const parsed = JSON.parse(rawBody);
            if (typeof parsed.message === 'string') {
                message = parsed.message;
            }
            else if (Array.isArray(parsed.message)) {
                message = parsed.message.join(', ');
            }
        }
        catch {
            if (looksLikeHtml(rawBody, contentType)) {
                message = 'API кабинета не отвечает. Обновите страницу чуть позже.';
            }
            else if (rawBody.trim()) {
                message = rawBody.trim();
            }
        }
        if (!csrfRetried &&
            response.status === 403 &&
            message === CSRF_MISMATCH_MESSAGE) {
            await reloadCsrfFromSession();
            return requestOnce(path, options, true);
        }
        if (response.status === 401) {
            csrfToken = null;
            onUnauthorized?.();
        }
        throw new ApiError(message, { status: response.status, path, method });
    }
    rememberCsrfHeader(response);
    if (response.status === 204) {
        return undefined;
    }
    return parseJsonBody(rawBody, contentType, path, method);
}
export async function requestBlob(path) {
    const response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok) {
        const rawBody = await response.text();
        let message = response.statusText || 'Request failed';
        try {
            const parsed = JSON.parse(rawBody);
            if (typeof parsed.message === 'string') {
                message = parsed.message;
            }
        }
        catch {
            if (rawBody.trim()) {
                message = rawBody.trim();
            }
        }
        if (response.status === 401) {
            csrfToken = null;
            onUnauthorized?.();
        }
        throw new ApiError(message, { status: response.status, path, method: 'GET' });
    }
    rememberCsrfHeader(response);
    return response.blob();
}
